<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api, type PlanDraft, type PlanPayload } from "../api";
import type { AgentWeekInput } from "../shared";
import PlanBuilder from "./PlanBuilder.vue";

const emit = defineEmits<{ error: [message: string]; toast: [message: string]; workoutStarted: [] }>();
const plan = ref<PlanPayload | null>(null);
const drafts = ref<PlanDraft[]>([]);
const loading = ref(true);
const copied = ref(false);
const editing = ref<{ week: AgentWeekInput; draftId?: string } | null>(null);
const startingDayId = ref<string | null>(null);
const activeWeek = computed(() => plan.value?.weeks[0] ?? null);
const activeDays = computed(() => activeWeek.value ? plan.value?.days.filter((day) => day.routine_week_id === activeWeek.value?.id) ?? [] : []);
const activeSessionDayId = computed(() => plan.value?.days.find((day) => day.session_status === "active")?.id ?? null);
function blocks(dayId: string) { return plan.value?.blocks.filter((block) => block.routine_day_id === dayId) ?? []; }
function displayDate(value: string) { return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(new Date(`${value}T12:00:00`)); }
function isoDate(value: Date): string { return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`; }

function nextMonday(): string {
  const value = new Date();
  value.setHours(12, 0, 0, 0);
  value.setDate(value.getDate() + ((8 - value.getDay()) % 7 || 7));
  return isoDate(value);
}

function blankWeek(): AgentWeekInput {
  const monday = nextMonday();
  return {
    week_start: monday, title: "Next training week", focus: "", coach_notes: "", warmup: [],
    days: [{ day_date: monday, title: "Workout 1", focus: "", estimated_minutes: 50, blocks: [{
      label: "A", kind: "superset", rounds: 3, rest_seconds: 90,
      exercises: [
        { name: "", target_sets: 3, rep_min: 8, rep_max: 10, target_rir: 2 },
        { name: "", target_sets: 3, rep_min: 8, rep_max: 10, target_rir: 2 }
      ]
    }] }]
  };
}

async function load() {
  loading.value = true;
  try {
    const [nextPlan, nextDrafts] = await Promise.all([api.plan(), api.drafts()]);
    plan.value = nextPlan;
    drafts.value = nextDrafts.drafts;
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not load plan"); }
  finally { loading.value = false; }
}

async function duplicateCurrent() {
  if (!activeWeek.value) return;
  try {
    const duplicate = structuredClone((await api.week(activeWeek.value.id)).week);
    const oldStart = new Date(`${duplicate.week_start}T12:00:00`);
    const newStart = new Date(oldStart); newStart.setDate(newStart.getDate() + 7);
    const delta = newStart.getTime() - oldStart.getTime();
    duplicate.week_start = isoDate(newStart);
    duplicate.title = `${duplicate.title} · next week`;
    duplicate.days.forEach((day) => { const date = new Date(`${day.day_date}T12:00:00`); day.day_date = isoDate(new Date(date.getTime() + delta)); });
    editing.value = { week: duplicate };
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not duplicate week"); }
}

async function saved() { emit("toast", "Draft saved"); await load(); }
async function published() { editing.value = null; emit("toast", "Workout week published"); await load(); }

async function startDay(day: PlanPayload["days"][number]) {
  startingDayId.value = day.id;
  try {
    await api.startSession(day.id);
    emit("toast", day.session_status === "active" ? "Workout resumed" : `${day.title} started`);
    emit("workoutStarted");
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not start workout"); }
  finally { startingDayId.value = null; }
}

function dayActionLabel(day: PlanPayload["days"][number]): string {
  if (startingDayId.value === day.id) return day.session_status === "active" ? "Resuming…" : "Starting…";
  if (day.session_status === "active") return "Resume workout";
  if (activeSessionDayId.value) return "Finish active workout first";
  if (day.session_status === "completed") return "Repeat workout";
  return "Start workout";
}

async function copyAgentPrompt() {
  await navigator.clipboard.writeText(`Use my Superset Workout Agent API at ${location.origin}/api/openapi.json. Authenticate with the personal token I provide. First call getPlanningContext and follow the returned coach_contract. Act as the training expert: treat my comments and preferences as evidence, preserve each movement's purpose, explain your recommendation, and push back respectfully when warranted. Then plan and replace my next workout week. Optimize for two-exercise supersets, preserve exercise names when useful for continuity, put session preparation in the warmup checklist, use duration_seconds for time-based work (never seconds as reps), and include rep ranges, target RIR, progression notes, short cues, and technique video URLs.`);
  copied.value = true;
  window.setTimeout(() => { copied.value = false; }, 1800);
}
onMounted(() => { void load(); });
</script>

<template>
  <section class="view plan-view">
    <PlanBuilder v-if="editing" :initial="editing.week" :draft-id="editing.draftId" @cancel="editing = null" @saved="saved" @published="published" @error="emit('error', $event)" />
    <template v-else>
      <header class="view-header"><div><p class="eyebrow">Programming</p><h1>This week</h1></div><a class="icon-button" href="/api/openapi.json" target="_blank" aria-label="Open agent API schema">{ }</a></header>
      <div class="plan-actions"><button class="button primary" @click="editing = { week: blankWeek() }">＋ Build a week</button><button v-if="activeWeek" class="button ghost" @click="duplicateCurrent">Duplicate current</button></div>
      <div v-if="loading" class="skeleton-stack"><i></i><i></i><i></i></div>
      <template v-else-if="activeWeek">
        <section class="week-card">
          <p class="eyebrow">Week of {{ displayDate(activeWeek.week_start) }} · {{ activeWeek.source }}</p>
          <h2>{{ activeWeek.title }}</h2><p>{{ activeWeek.focus }}</p>
          <div v-if="activeWeek.warmup.length" class="warmup-note plan-warmup"><span>WARM-UP</span><ul><li v-for="step in activeWeek.warmup" :key="step">{{ step }}</li></ul></div>
          <div v-if="activeWeek.coach_notes" class="week-note">{{ activeWeek.coach_notes }}</div>
        </section>
        <div class="days-list">
          <article v-for="(day, index) in activeDays" :key="day.id" class="day-card">
            <header><span>{{ String(index + 1).padStart(2, "0") }}</span><div><p>{{ displayDate(day.day_date) }}</p><h3>{{ day.title }}</h3></div><b>{{ day.estimated_minutes }}m</b></header>
            <p>{{ day.focus }}</p>
            <div v-for="block in blocks(day.id)" :key="block.id" class="plan-block"><b>{{ block.label }}</b><span>{{ block.exercise_names }}</span><small>{{ block.rounds }} rounds · {{ block.rest_seconds }}s</small></div>
            <button
              class="button day-start"
              :class="day.session_status === 'active' ? 'primary' : 'secondary'"
              :disabled="Boolean(activeSessionDayId && day.session_status !== 'active') || Boolean(startingDayId)"
              @click="startDay(day)"
            >
              {{ dayActionLabel(day) }}
            </button>
          </article>
        </div>
      </template>
      <section v-else-if="!loading" class="empty-plan"><h2>No published week yet</h2><p>Start from a clean builder or let your planning agent create one.</p></section>

      <section v-if="drafts.some((draft) => draft.status === 'draft')" class="drafts-card">
        <header><div><p class="eyebrow">Workbench</p><h2>Saved drafts</h2></div><span>{{ drafts.filter((draft) => draft.status === 'draft').length }}</span></header>
        <button v-for="draft in drafts.filter((item) => item.status === 'draft')" :key="draft.id" @click="editing = { week: draft.payload, draftId: draft.id }">
          <div><b>{{ draft.title }}</b><small>Week of {{ displayDate(draft.week_start) }} · edited {{ new Date(draft.updated_at).toLocaleDateString() }}</small></div><span>Continue →</span>
        </button>
      </section>

      <section class="agent-card">
        <div class="agent-orbit" aria-hidden="true"><span>✦</span></div>
        <div><p class="eyebrow">Agent planning bridge</p><h2>Let Codex write the week</h2><p>Give Codex a revocable personal token. It can read completed performance and publish a week without sharing your browser login.</p></div>
        <button class="button secondary" @click="copyAgentPrompt">{{ copied ? "Prompt copied" : "Copy setup prompt" }}</button>
        <div class="endpoint"><span>SCHEMA</span><code>/api/openapi.json</code></div>
        <div class="endpoint"><span>CONTEXT</span><code>/api/agent/context</code></div>
        <p class="agent-settings-note">Create and revoke agent credentials in Settings.</p>
      </section>
    </template>
  </section>
</template>
